import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { AuthenticationService } from '../../../core/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { ScriptService } from '../../../core/script.service';
import { CENTER_OF_EUROPE, GOOGLE_MAPS_CONTROL_SIZE, GxMarker, GxMVCobject, MapArtefact, MousePositionType } from '../../../utils/map/gx-map-utils';
import { isMouseOverElement, sendToGoogleAnalytics } from '../../../core/utilities';
import { Awaited, TypedSimpleChanges } from '../../../utils/typescript';
import { AddressListLocation } from '../../../models/address-list-location';
import { BaseMapService, FOCUSED_ROW_CLASS_NAME, MARKER_ID_DATA_ATTR, MARKER_Z_INDICES } from './base-map.service';
import { MapLocationInterface } from '../../../models/review-insights-models/map-location';
import { MAP_ID_STYLING } from '../../locations-skeleton/map/map-utils';

@Component({ template: '' })
export abstract class BaseMapComponent implements OnInit, OnDestroy, OnChanges {
  // multiple map instances must be rendered into different divs -- so each child should provide
  // its own ID
  @Input() renderIntoContainerId: string;

  @Input() shouldCluster: boolean = false;

  // OPTIONS
  @Input() hideSpinner: boolean = false;
  @Input() optInitZoom: number = 4;
  @Input() optInitCenter: { lat: number; lng: number } = CENTER_OF_EUROPE;

  public mapLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // fires after map has been completely loaded and it is safe to proceed with stuff that can only be run
  // afterwards
  protected _mapLoaded$ = new BehaviorSubject<boolean>(false);

  protected destroyed$: Subject<void> = new Subject<void>();

  // map properties
  mapsKey$ = this.authService.mapsApiKey$;
  map: google.maps.Map = null;

  // info window properties
  mapInfoWindow: google.maps.InfoWindow = null;
  infoWindowContent = {
    markerType: null,
    businessName: null,
    storeCode: null,
    address: null,
    mapsUrl: null,
    logo: null,
    reviewCount: 0.0,
    averageRating: -1.0,
  };
  @ViewChild('infoWindowTemplate')
  infoWindowTemplate: ElementRef;

  // spiderfier (all maps spiderfy at the moment)
  overlappingMarkerSpiderfier: any;

  protected onMarkerDblClick$: Subject<Element> = new Subject<Element>();

  protected debouncedOnMarkerMouseover$: BehaviorSubject<{
    location: AddressListLocation,
    marker: GxMarker
  }> = new BehaviorSubject<{
    location: AddressListLocation,
    marker: GxMarker
  }>(null);

  protected hoverTimeoutWithMarker: {
    marker: GxMarker;
    timeoutId: number
  } = null;

  // for tracking the mouse's position to be able to geometrically determine
  // if the mouse is above an element
  protected mousePosition$: BehaviorSubject<MousePositionType> = new BehaviorSubject({
    x: 0,
    y: 0,
  });

  // for easy access of the address objects & map markers
  protected mapArtefacts: MapArtefact[] = [];

  // language
  currentLanguage: string = this.translate.currentLang;

  loadMapIntervalId: number;

  protected constructor(
      public authService: AuthenticationService,
      protected translate: TranslateService,
      protected scriptService: ScriptService,
      protected changeDetector: ChangeDetectorRef,
      // could be base map service or any service that extends it
      // i.e. CC / CV / GMB-DIFF map service...
      protected baseMapServiceOrExtendedEquivalent: BaseMapService,
  ) {
  }

  ngOnInit() {
    // listen to _mapLoaded$ and proceed with other
    // stuff that can be run only after the map has been completely loaded
    this._mapLoaded$
        .pipe(takeUntil(this.destroyed$))
        .subscribe(isLoaded => {
          if (isLoaded) {
            this.onMapLoaded();
          }
        });

    // create a Google Map instance
    this.mapsKey$.pipe(
        filter((mapsKey) => !!mapsKey),
        takeUntil(this.destroyed$))
        .subscribe((mapsKey) => {
          this._mapLoaded$.next(false);
          this.initMap(mapsKey);
        });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    if (this.overlappingMarkerSpiderfier) {
      this.overlappingMarkerSpiderfier.forgetAllMarkers();
    }

    // clear interval and ID if one has been set up
    if (this.loadMapIntervalId) {
      window.clearInterval(this.loadMapIntervalId);
      this.loadMapIntervalId = null;
    }
  }

  ngOnChanges(changes: TypedSimpleChanges<BaseMapComponent>): void {
    // ngOnChanges is called pretty early so make sure
    // we've got all the requirements for initializing the logic which depends on
    // `this.map` being present.
    if ( // google window object
        typeof google !== 'undefined'
        // this will have been manually set after the tiles have been loaded
        && this._mapLoaded$.getValue()
        // ensure the existence of the map instance
        && this.map
        // implementation-specific checks
        && this.shouldReloadMarkersOnNgChanges(changes)
    ) {
      this.initLogic();
    }
  }

  /**
   * Called in ngOnChanges. Enables adding implementation-specific checks before calling
   * initLogic() on changes. If not overwritten, initLogic() will be called every time ngOnChanges() fires.
   * @param changes
   */
  protected abstract shouldReloadMarkersOnNgChanges(changes: TypedSimpleChanges<BaseMapComponent>): boolean;

  /*
   * LISTENERS
   */

  /**
   * This event feed controls the toggling of marker tooltips
   */
  protected listenToMarkerMouseoverEvent() {
    this.debouncedOnMarkerMouseover$
        .pipe(
            filter(val => Boolean(val)),
            distinctUntilChanged(),
            debounceTime(60),
            takeUntil(this.destroyed$),
        )
        .subscribe(({ location, marker }) => {
          this.markerOnMouseover(location, marker);
        });
  }

  /**
   * This event feed controls setting rows which map PINs were double-clicked into view
   */
  protected listenToMarkerDblClickEvent() {
    this.onMarkerDblClick$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((matRow) => {
          if (matRow) {
            matRow.scrollIntoView({ block: 'center' });
          }
        });
  }

  /**
   * We're listening to the mouse move event on the whole document because we need to
   * check for mouse positions in `mouseover` table ROW events -- used both in CC and in the
   * regular address list map (BLS-3503)
   */
  protected listenToDocumentMousemoveFeed() {
    // register an event to track the mouse position.
    fromEvent(document, 'mousemove')
        .pipe(takeUntil(this.destroyed$))
        .subscribe((e: MouseEvent) => {
          this.mousePosition$.next({ x: e.clientX, y: e.clientY });
        });
  }

  /**
   * Find or create the tooltip artefact and open it
   * @param location
   * @param marker
   * @private
   */
  private markerOnMouseover(
      location: AddressListLocation,
      marker: GxMarker) {
    const parentId = BaseMapService.extractParentIdString(location);

    // ensure that no tooltips are displayed at the same time
    this.closeAllTooltips(parentId);

    this.hoverTimeoutWithMarker = {
      marker,
      timeoutId: window.setTimeout(
          () => this.attemptHoverTimeoutCleanup(parentId),
          2500,
      ),
    };

    const isParent = BaseMapService.isParent(location);
    const {
      topLevelArtefact,
      parentIndex,
      childIndex,
    } = this.baseMapServiceOrExtendedEquivalent.findArtefactGroup(location, this.mapArtefacts);

    if (!topLevelArtefact) {
      BaseMapService._logSomethingWentWrong('markerOnMouseover', {
        location,
        topLevelArtefact,
        parentIndex,
        childIndex,
      });
      return;
    }

    let hoverTooltip: any;
    // deferred getter
    const getHoverTooltip = () => hoverTooltip;

    // register the listener before fetching/creating the hover
    // tooltip to be ready to react quickly enough
    const mouseoutListener = google.maps.event.addListener(
        marker,
        'mouseout',
        () => {
          // nothing to do here
          if (!getHoverTooltip()) return;

          // close & flush the listener
          hoverTooltip.close();
          mouseoutListener.remove();

          // revert the zIndex and let gmaps itself decide how stacked the markers will be
          marker.setOptions({
            zIndex: isParent ? MARKER_Z_INDICES.parent : MARKER_Z_INDICES.child,
            icon: marker.meta.icons.defaultIcon,
          });

          // just to make sure
          this.closeAllTooltips(parentId);
          this.attemptHoverTimeoutCleanup(parentId);
        });

    const TOOLTIP_CONTENT = `
       <div class="block text-center text-xs truncate">
          ${location.businessName}
       </div>
    `;

    if (isParent) {
      hoverTooltip = topLevelArtefact.parentLocation.hoverTooltip;
      if (hoverTooltip) {
        // already exists so open it
        hoverTooltip.open();
      } else {
        // create a fresh one
        hoverTooltip = this.baseMapServiceOrExtendedEquivalent.generateGxInfoWindow(
            GxMVCobject.TOOLTIP,
            marker.getPosition().toJSON(),
            this.map,
            {
              content: TOOLTIP_CONTENT,
            },
        );

        // do open
        hoverTooltip.open();

        // update the stateful artefacts at the corresponding position(s)
        this.baseMapServiceOrExtendedEquivalent.updateArtefact(
            { parentIndex },
            { hoverTooltip: hoverTooltip },
            this.mapArtefacts);
      }

      // use raw CSS to toggle the class
      const classList = document
          .querySelector(
              `mat-row[data-row-parent-id="${topLevelArtefact.parentLocation.id}"]`,
          )?.classList;

      classList?.remove(FOCUSED_ROW_CLASS_NAME);
      classList?.add(FOCUSED_ROW_CLASS_NAME);

    } else {
      if (childIndex === -1) {
        BaseMapService._logSomethingWentWrong(
            'markerOnMouseover',
            { location },
            'No child artefact');
        return;
      }

      hoverTooltip = topLevelArtefact.childrenLocations[childIndex].hoverTooltip;
      if (hoverTooltip) {
        // already exists so open it
        hoverTooltip.open();
      } else {
        // create a fresh one
        hoverTooltip = this.baseMapServiceOrExtendedEquivalent.generateGxInfoWindow(
            GxMVCobject.TOOLTIP,
            marker.getPosition().toJSON(),
            this.map,
            {
              content: TOOLTIP_CONTENT,
            },
        );

        // do open
        hoverTooltip.open();

        // update the stateful artefacts at the corresponding position(s)
        this.baseMapServiceOrExtendedEquivalent.updateArtefact({ parentIndex, childIndex },
            { hoverTooltip: hoverTooltip },
            this.mapArtefacts);
      }

      // use raw CSS to toggle the class
      // careful - element might not exist if gmaps matches update has been triggered
      // and regular child rows have changes to loading rows
      const classList = document
          .querySelector(`mat-row[data-row-safe-id="${
              topLevelArtefact.childrenLocations[childIndex].safeId
          }"]`)?.classList;

      classList?.remove(FOCUSED_ROW_CLASS_NAME);
      classList?.add(FOCUSED_ROW_CLASS_NAME);
    }

    // bring the marker to the front. 1e6 (1Mil) should be enough
    marker.setOptions({
      zIndex: MARKER_Z_INDICES.highlight,
      icon: marker.meta.icons.highlightedIcon,
    });
  }

  /**
   * LOGIC
   */

  /**
   * Load markers for locations or perform other biz logic.
   */
  protected abstract initLogic();

  /**
   * Stuff to run after map has been loaded (define spiderfier, init markers, etc.)
   */
  protected onMapLoaded() {
    this.initLogic();
  };

  protected initOverlappingMarkerSpiderfier() {
    const Spiderfier = (window as any).OverlappingMarkerSpiderfier;
    this.overlappingMarkerSpiderfier = new Spiderfier(this.map, {
      markersWontMove: true,
      markersWontHide: true,
      keepSpiderfied: true,
      ignoreMapClick: false,
      nearbyDistance: 25,
    });
  }

  /**
   * Lazy-loads Google Maps scripts, and loads the map afterwards.
   * @param mapsKey
   * @param language Language in which the map should be loaded. If null, it will be set to the
   * current user language.
   */
  initMap(mapsKey: string, language?: string) {
    this.mapLoading$.next(true);

    if (!language) {
      language = this.translate.currentLang;
    }
    this.currentLanguage = language;

    if (!mapsKey) {
      console.error('Maps key is null');
      this.mapLoading$.next(false);
      return;
    }

    // no need to load the map scripts again
    // so proceed straightaway to marker initialization
    if (this.map) {
      this._mapLoaded$.next(true);
      return;
    }

    this.loadScripts(mapsKey, language).then(() => this.loadMap());
  }

  /**
   * Loads all required scripts for the map (google script, spiderfier script and if needed also the
   * clusterer script) and returns a Promise that resolves after all of the scripts have been loaded.
   * @param mapsKey
   * @param language
   * @protected
   */
  protected loadScripts(mapsKey: string, language: string): Promise<Awaited<ReturnType<ScriptService['loadScript']>>[]> {
    const scripts$ = [this.loadMapScript(mapsKey, language), this.loadSpiderfierScript()];
    if (this.shouldCluster) {
      scripts$.push(this.loadMarkerClustererScript());
    }
    return Promise.all(scripts$);
  }

  /**
   * Load Marker Clusterer Script
   */
  protected loadMarkerClustererScript() {
    return this.scriptService
        .loadScript({
          url: 'https://unpkg.com/@google/markerclustererplus@4.0.1/dist/markerclustererplus.min.js',
        });
  }

  /**
   * Load Spiderfier Script
   */
  protected loadSpiderfierScript() {
    return this.scriptService.loadScript({
      url: 'https://cdnjs.cloudflare.com/ajax/libs/OverlappingMarkerSpiderfier/1.0.3/oms.js',
      async: false,
      defer: false,
    });
  }

  /**
   * Loads the base Google Map Script and places API
   */
  protected loadMapScript(mapsKey: string, language: string) {
    return this.scriptService.loadScript({
      url: `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&language=${language}&libraries=places`,
    });
  }

  /**
   * Initializes a Google Map instance and iterates _mapLoaded$ after the map is completely loaded.
   */
  protected loadMap() {
    if (
        // since `renderIntoContainerId` is an input without a default value,
        // we'll need to wait for it to become defined -- which happens one of the subsequent
        // `ngOnChanges` calls: https://stackoverflow.com/a/39219751/8160318
        !this.renderIntoContainerId ||
        !document.getElementById(this.renderIntoContainerId)) {
      // nowhere to render the map canvas into so schedule an interval in which to try again
      if (!this.loadMapIntervalId) {
        this.loadMapIntervalId = window.setInterval(() => this.loadMap(), 50);
        return;
      } else return;
    }

    // clear interval and ID
    if (this.loadMapIntervalId) {
      window.clearInterval(this.loadMapIntervalId);
      this.loadMapIntervalId = null;
    }

    this.mapLoading$.next(true);

    // load map
    this.map = new google.maps.Map(document.getElementById(this.renderIntoContainerId), {
      center: this.optInitCenter,
      zoom: this.optInitZoom,
      controlSize: GOOGLE_MAPS_CONTROL_SIZE,
      backgroundColor: 'white',
      fullscreenControl: false,
      clickableIcons: false,

      mapId: MAP_ID_STYLING,
      mapTypeControl: true,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControlOptions: {
        mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE, google.maps.MapTypeId.HYBRID],
        position: google.maps.ControlPosition.LEFT_TOP,
      },
      cameraControl: false,
      zoomControl: true
    });

    // add spiderfier
    if (!this.overlappingMarkerSpiderfier) {
      this.initOverlappingMarkerSpiderfier();
    }

    // wait for the initial map canvas paint first
    // and start the marker initialization only after that
    google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
      console.info('idle event fired so we can proceed w/ our map business logic');
      // the map is *truly* loaded now (visually)
      // proceed to init markers
      this._mapLoaded$.next(true);
    });
  }

  /**
   * Stop spinner and detect changes
   * Run after map and map artefacts are loaded
   */
  protected finishLoading() {
    this.mapLoading$.next(false);
    // we've got the onPush CD strategy to manually trigger change detection
    this.changeDetector.detectChanges();
  }

  /**
   * CLEANUP FUNCTIONS
   */

  /**
   * Gracefully clean up on-hover artefacts.
   * @param parentLocationId
   * @private
   */
  private attemptHoverTimeoutCleanup(parentLocationId?: MapArtefact['parentLocation']['id']) {
    if (!this.mapArtefacts.length) {
      // nothing to work with
      return false;
    }

    if (!this.hoverTimeoutWithMarker) {
      BaseMapService._logSomethingWentWrong(
          'attemptHoverTimeoutCleanup',
          {
            hoverTimeoutWithMarker: this.hoverTimeoutWithMarker,
          },
          'No `hoverTimeoutWithMarker`. Bailing out.',
      );
      return;
    }

    const markerId = this.hoverTimeoutWithMarker?.marker?.meta?.id;
    if (!markerId) {
      BaseMapService._logSomethingWentWrong(
          'attemptHoverTimeoutCleanup',
          {
            hoverTimeoutWithMarker: this.hoverTimeoutWithMarker,
          },
          'No `markerId`. Bailing out.',
      );
    }

    const isRowHovered = document
            // find the row based on the marker ID
            .querySelector(`[${MARKER_ID_DATA_ATTR}="${markerId}"]`)
            // jump back in the tree to its parent
            ?.closest('mat-row')
            // https://stackoverflow.com/a/46972433/8160318
            ?.matches(':hover')
        // or locate the row based on the marker's metadata - BLS-3503
        || document
            .querySelector(BaseMapService.constructRowSelectorString(this.hoverTimeoutWithMarker?.marker?.meta?.location))
            ?.matches(':hover');

    // select the marker image element (the img w/ the svg src)
    const markerImage = document.querySelector(
        `.mapContainer img[src*="${MARKER_ID_DATA_ATTR}=\\"${markerId}\\""]`,
    );

    // check whether the current mouse position on the window intersects
    // with the bounding rect of the marker image (svg)
    const isMarkerHovered = isMouseOverElement(
        this.mousePosition$.getValue(),
        markerImage,
    );

    // careful, there's a hover
    if (isRowHovered || isMarkerHovered) {
      // clear the current timeout
      clearTimeout(this.hoverTimeoutWithMarker?.timeoutId);

      // attempt the same cleanup strategy later
      this.hoverTimeoutWithMarker = {
        marker: this.hoverTimeoutWithMarker.marker,
        timeoutId: window.setTimeout(
            () => this.attemptHoverTimeoutCleanup(parentLocationId),
            2500,
        ),
      };

      console.debug(
          'Prevented tooltip close within hoverTimeoutWithMarker. Row or tooltip are hovered',
          { markerId, isRowHovered, isMarkerHovered });
      return;
    }

    console.debug('Closing all tooltips within hoverTimeoutWithMarker because it\'s safe to do so', {
      markerId,
    });
    this.closeAllTooltips();

    // clear the timeout
    clearTimeout(this.hoverTimeoutWithMarker?.timeoutId);

    // unset
    this.hoverTimeoutWithMarker = null;
  }

  /**
   * Close all tooltips
   */
  public closeAllTooltips(parentLocationId: MapArtefact['parentLocation']['id'] = undefined) {
    this.baseMapServiceOrExtendedEquivalent.findAllArtefacts(GxMVCobject.TOOLTIP, {
          parentId: parentLocationId,
        },
        this.mapArtefacts,
    ).map(tooltip => tooltip?.close());

    // flush the highlight class on the parents as well as children
    document
        .querySelectorAll('mat-row[data-row-parent-id], mat-row[data-row-safe-id]')
        .forEach((selector) => {
          selector.classList.remove(FOCUSED_ROW_CLASS_NAME);
        });

    this.baseMapServiceOrExtendedEquivalent.findAllArtefacts(GxMVCobject.MARKER, {
          parentsOnly: true,
          childrenOnly: false,
          parentId: parentLocationId,
        },
        this.mapArtefacts).filter(
        (marker: GxMarker) => {
          // filter those that are already correctly set
          // to prevent loads of mvc object updates
          return marker.getZIndex() !== MARKER_Z_INDICES.parent;
        },
    ).map(
        // revert the parent marker zIndices back down
        (marker: GxMarker) => marker?.setOptions({
          zIndex: MARKER_Z_INDICES.parent,
          icon: marker.meta.icons.defaultIcon,
        }),
    );

    this.baseMapServiceOrExtendedEquivalent.findAllArtefacts(GxMVCobject.MARKER, {
          parentsOnly: false,
          childrenOnly: true,
          parentId: parentLocationId,
        },
        this.mapArtefacts).filter(
        (marker: GxMarker) => {
          // filter those that are already correctly set
          // to prevent loads of mvc object updates
          return marker.getZIndex() !== MARKER_Z_INDICES.child;
        },
    ).map(
        // revert the child marker zIndices back down
        (marker: GxMarker) => marker?.setOptions({
          zIndex: MARKER_Z_INDICES.child,
          icon: marker.meta.icons.defaultIcon,
        }),
    );
  }

  /**
   * Destroy all map bindings like markers, listeners, timers, etc.
   * @private
   */
  protected cleanupAllArtefacts(): void {
    this.closeAllTooltips();
    this.attemptHoverTimeoutCleanup();
    this.mapArtefacts = BaseMapService.flushMapArtefacts(this.mapArtefacts, this.overlappingMarkerSpiderfier);
  }

  /**
   * UTILS
   */
  /**
   * Constructs an address string by appending address-relevant parts, if available.
   */
  protected static constructAddress(location: MapLocationInterface): string {
    let address = '';

    if (location.addressLines) {
      address = location.addressLines.split(',').join('\n') + '\n';
    }

    if (location.postalCode) {
      address += `${location.postalCode} `;
    }

    if (location.city) {
      address += `${location.city}${location.country ? ', ' : ''}`;
    }

    if (location.country) {
      address += location.country;
    }

    return address;
  }

  /**
   * Generates the text that should be displayed when clicking on a marker on the map.
   * @param marker Location for which the info text should be generated
   */
  protected openMapInfoWindow(marker) {
    const location: MapLocationInterface = marker.location || marker.meta?.location;

    if (!location) {
      console.warn('Couldn\'t target a location to open an infowindow for...',
          { marker });
      return;
    }

    this.infoWindowContent.address = BaseMapComponent.constructAddress(location);
    this.infoWindowContent.businessName = location.businessName;
    this.infoWindowContent.storeCode = location.storeCode;
    this.infoWindowContent.mapsUrl = location.mapsUrl;
    this.infoWindowContent.logo = location.logoPhoto;
    this.infoWindowContent.reviewCount = location.totalReviews;
    this.infoWindowContent.averageRating = location.avgRating;

    // open info window while reviews are still loading
    this.changeDetector.detectChanges();
    this.mapInfoWindow.setContent(this.infoWindowTemplate.nativeElement.innerHTML);

    this.mapInfoWindow.open(this.map, marker);
    sendToGoogleAnalytics('Map Interaction',
        'open info window',
        'Open info window on map',
    );
  }

  /**
   * Closes the info window of a marker on the map.
   */
  protected closeMapInfoWindow() {
    if (this.mapInfoWindow) {
      this.mapInfoWindow.close();
    }
  }

}
